import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  FileText,
  Eye,
  Calendar,
  Tag,
  HardDrive,
  Building2,
  Clock,
  Pencil,
  Save,
  Loader2,
  FolderOpen,
  User,
  Shield,
  Download,
  ExternalLink,
} from "lucide-react";
import { hasPermission } from "../../../utils/permissionUtils";
import { getDocumentGroups } from "../../../api/hrm/documents/groups";
import { getDocumentById, updateDocument } from "../../../api/hrm/documents/info";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/ConfirmDialog";
import { formatDateVN, formatDateTimeVN } from "../../../utils/formatUtils";
import DocumentAccessPicker from "./DocumentAccessPicker";

const formatFileSize = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(n) / Math.log(k));
  return `${parseFloat((n / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const fileExtension = (name) => {
  if (!name || typeof name !== "string") return "";
  const i = name.lastIndexOf(".");
  return i >= 0 ? name.slice(i + 1).toUpperCase() : "";
};

const MetaRow = ({ icon: Icon, label, value }) => (
  <div className="flex gap-3 py-2.5 border-b border-gray-50 last:border-b-0">
    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-600">
      <Icon size={15} strokeWidth={2.25} />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
        {label}
      </p>
      <p className="mt-0.5 text-sm font-semibold text-gray-800 break-words leading-snug">
        {value || "—"}
      </p>
    </div>
  </div>
);

const FieldLabel = ({ children }) => (
  <label className="text-xs font-semibold text-gray-600">{children}</label>
);

const inputClass =
  "mt-1.5 w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100";

const DocumentDetailModal = ({ document: docItem, onClose, onUpdated }) => {
  const canUpdate = hasPermission("hrm.documents", "U");
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [groupOptions, setGroupOptions] = useState([]);
  const [folderId, setFolderId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [accessAll, setAccessAll] = useState(true);
  const [accessBranchIds, setAccessBranchIds] = useState([]);
  const [accessDepartmentIds, setAccessDepartmentIds] = useState([]);
  const [accessSummary, setAccessSummary] = useState("Tất cả chi nhánh / bộ phận");
  const [liveDoc, setLiveDoc] = useState(null);
  const [formData, setFormData] = useState({
    file_name: "",
    description: "",
    issue_date: "",
    effective_from: "",
    effective_to: "",
    issuer: "",
    tags: "",
    status: "active",
    group_id: "",
  });

  const resolveFolderCategory = (groups, selectedId) => {
    if (!selectedId) return { folderId: "", categoryId: "" };
    const node = groups.find((g) => g.id === selectedId);
    if (!node) return { folderId: selectedId, categoryId: "" };
    if (node.parent_id) return { folderId: node.parent_id, categoryId: node.id };
    return { folderId: node.id, categoryId: "" };
  };

  const applyAccess = (payload) => {
    const all =
      payload?.access_all === undefined || payload?.access_all === null
        ? true
        : Boolean(payload.access_all);
    const branches = Array.isArray(payload?.access_branch_ids)
      ? payload.access_branch_ids
      : [];
    const depts = Array.isArray(payload?.access_department_ids)
      ? payload.access_department_ids
      : [];
    setAccessAll(all);
    setAccessBranchIds(branches);
    setAccessDepartmentIds(depts);
    if (all) {
      setAccessSummary("Tất cả chi nhánh / bộ phận");
    } else {
      const parts = [];
      if (branches.length) parts.push(`${branches.length} chi nhánh`);
      if (depts.length) parts.push(`${depts.length} bộ phận`);
      setAccessSummary(
        parts.length ? `Giới hạn: ${parts.join(", ")}` : "Chưa cấu hình phạm vi"
      );
    }
  };

  useEffect(() => {
    if (!docItem) return;
    setIsEditing(false);
    setLiveDoc(docItem);
    setFormData({
      file_name: docItem.file_name || "",
      description: docItem.description || "",
      issue_date: docItem.issue_date
        ? String(docItem.issue_date).slice(0, 10)
        : "",
      effective_from: docItem.effective_from
        ? String(docItem.effective_from).slice(0, 10)
        : "",
      effective_to: docItem.effective_to
        ? String(docItem.effective_to).slice(0, 10)
        : "",
      issuer: docItem.issuer || "",
      tags: docItem.tags || "",
      status: docItem.status || "active",
      group_id: docItem.group_id || "",
    });
    applyAccess(docItem);

    let cancelled = false;
    (async () => {
      try {
        const [full, groupsRes] = await Promise.all([
          docItem.id ? getDocumentById(docItem.id) : null,
          getDocumentGroups(),
        ]);
        if (cancelled) return;
        const items = Array.isArray(groupsRes)
          ? groupsRes
          : groupsRes?.items || groupsRes?.data || [];
        setGroupOptions(items);
        if (full) {
          setLiveDoc((prev) => ({ ...prev, ...full }));
          applyAccess(full);
          setFormData((prev) => ({
            ...prev,
            file_name: full.file_name || prev.file_name,
            description: full.description ?? prev.description,
            issue_date: full.issue_date
              ? String(full.issue_date).slice(0, 10)
              : prev.issue_date,
            effective_from: full.effective_from
              ? String(full.effective_from).slice(0, 10)
              : prev.effective_from,
            effective_to: full.effective_to
              ? String(full.effective_to).slice(0, 10)
              : prev.effective_to,
            issuer: full.issuer ?? prev.issuer,
            tags: full.tags ?? prev.tags,
            status: full.status || prev.status,
            group_id: full.group_id || prev.group_id,
          }));
        }
      } catch {
        /* giữ data từ list */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [docItem]);

  useEffect(() => {
    if (!isEditing) return;
    const resolved = resolveFolderCategory(groupOptions, formData.group_id);
    setFolderId(resolved.folderId);
    setCategoryId(resolved.categoryId);
  }, [isEditing, groupOptions, formData.group_id]);

  const doc = liveDoc || docItem;

  const folderSelectOptions = useMemo(
    () =>
      groupOptions
        .filter((g) => !g.parent_id)
        .sort((a, b) =>
          String(a.group_name || "").localeCompare(
            String(b.group_name || ""),
            "vi"
          )
        ),
    [groupOptions]
  );

  const categorySelectOptions = useMemo(
    () =>
      groupOptions
        .filter((g) => g.parent_id === folderId)
        .sort((a, b) =>
          String(a.group_name || "").localeCompare(
            String(b.group_name || ""),
            "vi"
          )
        ),
    [groupOptions, folderId]
  );

  const statusLabel = useMemo(() => {
    if (doc?.status === "active") return "Hiệu lực";
    if (doc?.status === "expired") return "Hết hiệu lực";
    if (doc?.status === "inactive") return "Ngừng dùng";
    return doc?.status || "—";
  }, [doc?.status]);

  const statusStyles = useMemo(() => {
    if (doc?.status === "active")
      return "bg-emerald-50 text-emerald-800 border-emerald-200";
    if (doc?.status === "expired")
      return "bg-amber-50 text-amber-900 border-amber-200";
    if (doc?.status === "inactive")
      return "bg-gray-100 text-gray-600 border-gray-200";
    return "bg-slate-50 text-slate-600 border-slate-200";
  }, [doc?.status]);

  const groupLabel = useMemo(() => {
    if (!doc?.group_id) return "Tài liệu khác";
    const node = groupOptions.find((g) => g.id === doc.group_id);
    if (node?.parent_id) {
      const parent = groupOptions.find((g) => g.id === node.parent_id);
      return parent
        ? `${parent.group_name} / ${node.group_name}`
        : node.group_name;
    }
    if (node) return node.group_name;
    return doc.group_name || "—";
  }, [doc, groupOptions]);

  const ext = fileExtension(doc?.file_name);
  const mime = String(doc?.file_type || "").toLowerCase();
  const isPdf =
    String(ext || "").toLowerCase() === "pdf" || mime.includes("pdf");
  const isZip =
    String(ext || "").toLowerCase() === "zip" ||
    mime.includes("zip") ||
    mime === "application/x-zip-compressed";

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!doc?.id) return;
    if (!formData.file_name.trim())
      return showErrorToast("Tên tài liệu không được để trống.");
    if (
      formData.effective_from &&
      formData.effective_to &&
      new Date(formData.effective_from) > new Date(formData.effective_to)
    ) {
      return showErrorToast("Khoảng ngày hiệu lực không hợp lệ.");
    }
    if (
      !accessAll &&
      accessBranchIds.length === 0 &&
      accessDepartmentIds.length === 0
    ) {
      return showErrorToast(
        "Hãy chọn ít nhất một Chi nhánh hoặc Bộ phận được xem, hoặc tick «Tất cả»."
      );
    }
    try {
      setSaving(true);
      const finalGroupId = categoryId || folderId || null;
      const payload = {
        file_name: formData.file_name.trim(),
        description: formData.description.trim(),
        issue_date: formData.issue_date || null,
        effective_from: formData.effective_from || null,
        effective_to: formData.effective_to || null,
        issuer: formData.issuer.trim(),
        tags: formData.tags.trim(),
        status: formData.status || "active",
        group_id: finalGroupId,
        access_all: accessAll,
        access_branch_ids: accessAll ? [] : accessBranchIds,
        access_department_ids: accessAll ? [] : accessDepartmentIds,
      };
      await updateDocument(doc.id, payload);
      showSuccessToast("Cập nhật tài liệu thành công.");
      setIsEditing(false);
      setLiveDoc((prev) => ({
        ...prev,
        ...payload,
        group_name: groupOptions.find((g) => g.id === finalGroupId)?.group_name,
      }));
      setAccessSummary(
        accessAll
          ? "Tất cả chi nhánh / bộ phận"
          : `Giới hạn: ${[
              accessBranchIds.length
                ? `${accessBranchIds.length} chi nhánh`
                : null,
              accessDepartmentIds.length
                ? `${accessDepartmentIds.length} bộ phận`
                : null,
            ]
              .filter(Boolean)
              .join(", ")}`
      );
      onUpdated?.();
    } catch (error) {
      console.error("Update document failed:", error);
      showErrorToast(
        error?.response?.data?.message || "Không thể cập nhật tài liệu."
      );
    } finally {
      setSaving(false);
    }
  };

  const tagList = useMemo(() => {
    if (!doc?.tags) return [];
    return String(doc.tags)
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  }, [doc?.tags]);

  if (typeof window === "undefined" || !window.document?.body) return null;

  return createPortal(
    <AnimatePresence>
      {docItem && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[9990] bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Mobile: bottom sheet · Desktop: centered modal */}
          <div className="fixed inset-0 z-[9999] flex items-end justify-center p-0 pointer-events-none sm:items-center sm:p-4">
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-labelledby="hrm-doc-detail-title"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 320 }}
              className="pointer-events-auto flex h-[94dvh] max-h-[94dvh] w-full max-w-6xl flex-col overflow-hidden rounded-t-[2rem] border border-gray-200 bg-white shadow-2xl sm:h-[min(860px,90vh)] sm:max-h-[90vh] sm:rounded-[2rem]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Grabber — mobile bottom sheet */}
              <div className="flex shrink-0 justify-center pt-2.5 pb-1 sm:hidden" aria-hidden>
                <div className="h-1 w-10 rounded-full bg-gray-300" />
              </div>

              {/* HEADER */}
              <div className="shrink-0 border-b border-gray-100 bg-gradient-to-r from-red-50/90 via-white to-white px-4 py-3.5 sm:px-6 sm:py-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 flex-1 items-start gap-3 sm:gap-3.5">
                    <div className="flex h-11 w-11 sm:h-12 sm:w-12 shrink-0 items-center justify-center rounded-2xl bg-red-600 shadow-md shadow-red-200">
                      <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-white" strokeWidth={2} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-red-600/80">
                        Chi tiết tài liệu
                      </p>
                      <h2
                        id="hrm-doc-detail-title"
                        className="mt-0.5 text-base font-bold leading-snug text-gray-900 sm:text-lg line-clamp-2"
                      >
                        {isEditing ? formData.file_name || doc.file_name : doc.file_name}
                      </h2>
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        <span
                          className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${statusStyles}`}
                        >
                          {statusLabel}
                        </span>
                        {ext ? (
                          <span className="inline-flex items-center rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[10px] font-bold uppercase text-gray-600">
                            {ext}
                          </span>
                        ) : null}
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500">
                          <HardDrive size={12} />
                          {formatFileSize(doc.file_size)}
                        </span>
                        <span className="hidden sm:inline-flex items-center gap-1 text-[11px] font-medium text-gray-400 truncate max-w-[220px]">
                          <FolderOpen size={12} className="shrink-0 text-red-400" />
                          {groupLabel}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="shrink-0 rounded-xl p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                    aria-label="Đóng"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* BODY */}
              <div className="min-h-0 flex-1 overflow-y-auto lg:overflow-hidden custom-scrollbar">
                {!isEditing ? (
                  <div className="grid min-h-full grid-cols-1 lg:h-full lg:min-h-0 lg:grid-cols-5">
                    {/* Preview — trên mobile xếp sau metadata để ưu tiên thông tin */}
                    <div className="order-2 lg:order-1 lg:col-span-3 flex flex-col min-h-[220px] lg:min-h-0 border-t lg:border-t-0 lg:border-r border-gray-100 bg-slate-50/70">
                      <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-gray-100 bg-white/80">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                          Xem trước
                        </p>
                        {doc.file_url && !isZip ? (
                          <a
                            href={doc.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 hover:underline"
                          >
                            Mở tab mới
                            <ExternalLink size={12} />
                          </a>
                        ) : null}
                      </div>
                      <div className="flex-1 min-h-0 p-3 sm:p-4">
                        {isPdf && doc.file_url ? (
                          <iframe
                            title={doc.file_name || "PDF preview"}
                            src={`${doc.file_url}#toolbar=1&navpanes=0`}
                            className="h-[42vh] lg:h-full min-h-[220px] lg:min-h-[420px] w-full rounded-xl border border-gray-200 bg-white shadow-sm"
                          />
                        ) : (
                          <div className="h-[36vh] lg:h-full min-h-[180px] lg:min-h-[420px] rounded-xl border border-dashed border-gray-200 bg-white flex flex-col items-center justify-center gap-3 px-6 text-center">
                            <div className="h-14 w-14 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center">
                              <FileText size={28} />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-gray-800">
                                {isZip
                                  ? "File nén — không xem trước được"
                                  : "Không có bản xem trước"}
                              </p>
                              <p className="mt-1 text-xs text-gray-500">
                                Dùng nút tải / mở file bên dưới.
                              </p>
                            </div>
                            {doc.file_url ? (
                              <a
                                href={doc.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2 text-xs font-bold uppercase tracking-wider text-white hover:bg-red-700"
                              >
                                <Download size={14} />
                                Tải tệp
                              </a>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Metadata — mobile hiện trước */}
                    <div className="order-1 lg:order-2 lg:col-span-2 flex flex-col min-h-0 lg:overflow-y-auto custom-scrollbar bg-white">
                      <div className="px-5 py-4 space-y-1">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">
                          Thông tin
                        </p>
                        <MetaRow
                          icon={FolderOpen}
                          label="Nhóm / Phân loại"
                          value={groupLabel}
                        />
                        <MetaRow
                          icon={Calendar}
                          label="Ngày ban hành"
                          value={formatDateVN(doc.issue_date)}
                        />
                        <MetaRow
                          icon={Building2}
                          label="Cơ sở ban hành"
                          value={doc.issuer}
                        />
                        <MetaRow
                          icon={Clock}
                          label="Hiệu lực"
                          value={`${formatDateVN(doc.effective_from) || "—"} → ${
                            formatDateVN(doc.effective_to) || "—"
                          }`}
                        />
                        <MetaRow
                          icon={User}
                          label="Người tải lên"
                          value={
                            doc.uploader_name
                              ? `${doc.uploader_name}${
                                  doc.uploaded_at
                                    ? ` · ${formatDateTimeVN(doc.uploaded_at)}`
                                    : ""
                                }`
                              : formatDateTimeVN(doc.uploaded_at)
                          }
                        />
                        <MetaRow
                          icon={Shield}
                          label="Phạm vi được xem"
                          value={accessSummary}
                        />
                      </div>

                      <div className="px-5 pb-3">
                        <div className="rounded-2xl border border-gray-100 bg-slate-50/80 p-3.5">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                            Mô tả
                          </p>
                          <p className="mt-1.5 text-sm leading-relaxed text-gray-700 whitespace-pre-wrap">
                            {doc.description?.trim()
                              ? doc.description
                              : "Không có mô tả."}
                          </p>
                        </div>
                      </div>

                      <div className="px-5 pb-4">
                        <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-2">
                          <Tag size={12} />
                          Tags
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {tagList.length ? (
                            tagList.map((tag, idx) => (
                              <span
                                key={`${tag}-${idx}`}
                                className="rounded-md bg-red-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-700 ring-1 ring-red-100"
                              >
                                {tag}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-gray-400">Chưa gắn thẻ</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="h-full overflow-y-auto px-5 py-5 sm:px-6 custom-scrollbar">
                    <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-4">
                      Chỉnh sửa thông tin (không thay đổi file đính kèm)
                    </p>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 max-w-4xl">
                      <div className="md:col-span-2">
                        <FieldLabel>Tên tài liệu</FieldLabel>
                        <input
                          value={formData.file_name}
                          onChange={(e) =>
                            handleChange("file_name", e.target.value)
                          }
                          className={inputClass}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <FieldLabel>Mô tả</FieldLabel>
                        <textarea
                          rows={3}
                          value={formData.description}
                          onChange={(e) =>
                            handleChange("description", e.target.value)
                          }
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <FieldLabel>Ngày ban hành</FieldLabel>
                        <input
                          type="date"
                          value={formData.issue_date}
                          onChange={(e) =>
                            handleChange("issue_date", e.target.value)
                          }
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <FieldLabel>Cơ sở ban hành</FieldLabel>
                        <input
                          value={formData.issuer}
                          onChange={(e) =>
                            handleChange("issuer", e.target.value)
                          }
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <FieldLabel>Hiệu lực từ</FieldLabel>
                        <input
                          type="date"
                          value={formData.effective_from}
                          onChange={(e) =>
                            handleChange("effective_from", e.target.value)
                          }
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <FieldLabel>Hiệu lực đến</FieldLabel>
                        <input
                          type="date"
                          value={formData.effective_to}
                          onChange={(e) =>
                            handleChange("effective_to", e.target.value)
                          }
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <FieldLabel>Nhóm tài liệu</FieldLabel>
                        <select
                          value={folderId}
                          onChange={(e) => {
                            const next = e.target.value;
                            setFolderId(next);
                            setCategoryId("");
                            handleChange("group_id", next || "");
                          }}
                          className={inputClass}
                        >
                          <option value="">Tài liệu khác</option>
                          {folderSelectOptions.map((group) => (
                            <option key={group.id} value={group.id}>
                              {group.group_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <FieldLabel>Phân loại</FieldLabel>
                        <select
                          value={categoryId}
                          disabled={!folderId}
                          onChange={(e) => {
                            const next = e.target.value;
                            setCategoryId(next);
                            handleChange("group_id", next || folderId || "");
                          }}
                          className={`${inputClass} disabled:bg-gray-50 disabled:text-gray-400`}
                        >
                          <option value="">
                            {folderId
                              ? categorySelectOptions.length
                                ? "-- Không chọn (gắn vào Nhóm) --"
                                : "Nhóm chưa có phân loại"
                              : "Chọn Nhóm tài liệu trước"}
                          </option>
                          {categorySelectOptions.map((group) => (
                            <option key={group.id} value={group.id}>
                              {group.group_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <FieldLabel>Trạng thái</FieldLabel>
                        <select
                          value={formData.status}
                          onChange={(e) =>
                            handleChange("status", e.target.value)
                          }
                          className={inputClass}
                        >
                          <option value="active">Hiệu lực</option>
                          <option value="expired">Hết hiệu lực</option>
                          <option value="inactive">Ngừng dùng</option>
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <FieldLabel>Tags</FieldLabel>
                        <input
                          value={formData.tags}
                          onChange={(e) => handleChange("tags", e.target.value)}
                          placeholder="tag1, tag2, tag3"
                          className={inputClass}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <DocumentAccessPicker
                          accessAll={accessAll}
                          branchIds={accessBranchIds}
                          departmentIds={accessDepartmentIds}
                          disabled={saving}
                          onChange={({
                            accessAll: nextAll,
                            branchIds,
                            departmentIds,
                          }) => {
                            setAccessAll(nextAll);
                            setAccessBranchIds(branchIds);
                            setAccessDepartmentIds(departmentIds);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* FOOTER — safe-area cho mobile */}
              <div className="shrink-0 border-t border-gray-100 bg-gray-50/90 px-4 py-3 sm:px-6 sm:py-3.5 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => (isEditing ? setIsEditing(false) : onClose())}
                    className="rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-xs font-bold uppercase tracking-wider text-gray-600 hover:bg-gray-50 active:scale-95 transition-all"
                  >
                    {isEditing ? "Hủy" : "Đóng"}
                  </button>
                  <div className="flex items-center gap-2">
                    {canUpdate && !isEditing && (
                      <button
                        type="button"
                        onClick={() => setIsEditing(true)}
                        className="hidden sm:inline-flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-xs font-bold uppercase tracking-wider text-gray-800 hover:border-red-200 hover:bg-red-50 active:scale-95 transition-all"
                      >
                        <Pencil size={15} />
                        <span>Chỉnh sửa</span>
                      </button>
                    )}
                    {canUpdate && isEditing && (
                      <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={saving}
                        className="hidden sm:inline-flex items-center justify-center gap-1.5 rounded-xl bg-red-600 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white shadow-sm shadow-red-200 hover:bg-red-700 disabled:opacity-60 active:scale-95 transition-all"
                      >
                        {saving ? (
                          <Loader2 size={15} className="animate-spin" />
                        ) : (
                          <Save size={15} />
                        )}
                        <span>Lưu thay đổi</span>
                      </button>
                    )}
                    {!isEditing && doc.file_url && !isZip ? (
                      <a
                        href={doc.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-red-600 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white shadow-sm shadow-red-200 hover:bg-red-700 active:scale-95 transition-all"
                      >
                        <Eye size={15} />
                        <span>Xem file</span>
                      </a>
                    ) : null}
                    {!isEditing && doc.file_url && isZip ? (
                      <a
                        href={doc.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-red-600 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white shadow-sm shadow-red-200 hover:bg-red-700 active:scale-95 transition-all"
                      >
                        <Download size={15} />
                        <span>Tải về</span>
                      </a>
                    ) : null}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    window.document.body
  );
};

export default DocumentDetailModal;
